import { inject } from '@angular/core';
import { CanActivateFn, RedirectFunction, Router } from '@angular/router';
import { map, Observable, of } from 'rxjs';
import { Permission } from '../models/session.model';
import { SessionStore } from './session.store';

// Every route but /sign-in: waits for the first load(), lets a signed-in user through,
// otherwise sends them to sign in and back to where they were going
export const sessionGuard: CanActivateFn = (route, state) => {
  const store = inject(SessionStore);
  const router = inject(Router);

  return loaded(store).pipe(
    map(() => store.state.status === 'signedIn'
      || router.createUrlTree(['/sign-in'], { queryParams: { returnUrl: state.url } }))
  );
};

// /sign-in only: a signed-in user has no business there
export const signedOutGuard: CanActivateFn = route => {
  const store = inject(SessionStore);
  const router = inject(Router);

  return loaded(store).pipe(
    map(() => store.state.status !== 'signedIn'
      || router.parseUrl(returnUrlOf(route.queryParamMap.get('returnUrl'))))
  );
};

// After sessionGuard, for a route that needs more than a session (/settings/users): without the
// permission the page is simply not theirs — back to the default page, no error shown
export function permissionGuard(permission: Permission): CanActivateFn {
  return () => {
    const store = inject(SessionStore);
    const router = inject(Router);

    return loaded(store).pipe(
      map(() => store.can(permission) || router.parseUrl('/'))
    );
  };
}

// The default route follows what the member may do, not their role name: a Planista lands in the BOM,
// a Brygadzista in production. Signed out, the plain /sign-in brings them back here afterwards
export const homeRedirect: RedirectFunction = () => {
  const store = inject(SessionStore);

  return loaded(store).pipe(
    map(() => store.state.status !== 'signedIn'
      ? '/sign-in'
      : store.can('ProductionOrdersWrite') ? '/bom' : '/production')
  );
};

// Only a local path is followed — a full URL in returnUrl would be an open redirect
export function returnUrlOf(returnUrl: string | null): string {
  return returnUrl?.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '/';
}

function loaded(store: SessionStore): Observable<void> {
  return store.state.status === 'unknown' ? store.load() : of(undefined);
}
